/**
 * Locale-aware URLs for the hosted legal pages.
 *
 * The website (fluencia.io) hosts an English canonical (`/privacy`, `/terms`)
 * plus a Spanish copy under `/es/…`, and it already auto-detects the browser
 * language on its own. These helpers make the *app* open the copy that matches
 * its current UI language, so a Spanish UI never lands on the English page —
 * the explicit-signal counterpart to the site's browser detection. It matters
 * when the app language was overridden away from the device language (the app
 * otherwise follows the OS language — see PR #145). Any non-Spanish UI keeps
 * the English canonical.
 */
import i18n from 'i18next';

import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from './constants';

const WEBSITE_ORIGIN = 'https://fluencia.io';

/** True when the resolved UI language is any Spanish variant (es, es-419, es-ES, …). */
function isSpanishUi(): boolean {
  return (i18n.language ?? '').toLowerCase().startsWith('es');
}

/** Insert the `/es` locale segment after the origin for a Spanish UI. */
function localizeForUi(url: string): string {
  if (!isSpanishUi()) return url;
  if (!url.startsWith(`${WEBSITE_ORIGIN}/`)) return url;
  return `${WEBSITE_ORIGIN}/es${url.slice(WEBSITE_ORIGIN.length)}`;
}

/** Privacy Policy URL for the app's current UI language. */
export function getPrivacyPolicyUrl(): string {
  return localizeForUi(PRIVACY_POLICY_URL);
}

/** Terms of Service URL for the app's current UI language. */
export function getTermsOfServiceUrl(): string {
  return localizeForUi(TERMS_OF_SERVICE_URL);
}
